import systemGroupsStoreV1 from './store'
import { IStore } from './core/interface/IStore'
import { ISysGroupsModule } from '@/contracts/system-modules/groups/ISysGroupsModule'
import { ISysAuthModule } from '@/contracts/system-modules/auth/ISysAuthModule'
import { ISysConsoleModule } from '@/contracts/system-modules/console/ISysConsoleModule'
import { EnumConsoleType } from '@/contracts/system-modules/console/enum/EnumConsoleType'
import { EnumGroupRole } from '@/contracts/system-modules/groups/enum/EnumGroupRole'
import { IGroup } from '@/contracts/system-modules/groups/IGroup'
import { IMember } from '@/contracts/system-modules/groups/IMember'
import { IInvite } from '@/contracts/system-modules/groups/IInvite'
import { ApiResponse } from '@/contracts/core/api/entity/ApiResponse'
import { SysAuthProviderModule } from '@/system-modules/auth/SysAuthProvider'
import { SysConsoleProviderModule } from '@/system-modules/console/ConsoleProvider'
import { GetGroupsRoute } from '@/system-modules/groups/v1/api/v1/groups/GetGroups/GetGroupsRoute'
import { CreateGroupRoute } from '@/system-modules/groups/v1/api/v1/groups/CreateGroup/CreateGroupRoute'
import { CreateGroupParams } from '@/system-modules/groups/v1/api/v1/groups/CreateGroup/Dto/CreateGroupParams'
import { RenameGroupRoute } from '@/system-modules/groups/v1/api/v1/groups/RenameGroup/RenameGroupRoute'
import { RenameGroupParams } from '@/system-modules/groups/v1/api/v1/groups/RenameGroup/Dto/RenameGroupParams'
import { RemoveGroupRoute } from '@/system-modules/groups/v1/api/v1/groups/RemoveGroup/RemoveGroupRoute'
import { GetMembersRoute } from '@/system-modules/groups/v1/api/v1/groups/GetMembers/GetMembersRoute'
import { ConfirmMemberRoute } from '@/system-modules/groups/v1/api/v1/groups/ConfirmMember/ConfirmMemberRoute'
import { RemoveMemberRoute } from '@/system-modules/groups/v1/api/v1/groups/RemoveMember/RemoveMemberRoute'
import { LeaveGroupRoute } from '@/system-modules/groups/v1/api/v1/groups/LeaveGroup/LeaveGroupRoute'
import { GetInvitesRoute } from '@/system-modules/groups/v1/api/v1/groups/GetInvites/GetInvitesRoute'
import { CreateInviteRoute } from '@/system-modules/groups/v1/api/v1/groups/CreateInvite/CreateInviteRoute'
import { CreateInviteParams } from '@/system-modules/groups/v1/api/v1/groups/CreateInvite/Dto/CreateInviteParams'
import { RemoveInviteRoute } from '@/system-modules/groups/v1/api/v1/groups/RemoveInvite/RemoveInviteRoute'
import { ApplyInviteRoute } from '@/system-modules/groups/v1/api/v1/groups/ApplyInvite/ApplyInviteRoute'
import { ApplyInviteParams } from '@/system-modules/groups/v1/api/v1/groups/ApplyInvite/Dto/ApplyInviteParams'

export class SysGroupsV1 implements ISysGroupsModule {
  private static instance: ISysGroupsModule
  private store: IStore = systemGroupsStoreV1()
  private auth: () => ISysAuthModule
  private console: ISysConsoleModule

  /** Модуль auth берётся при обращении: он создаётся раньше и сам от groups не зависит */
  constructor(
    auth: () => ISysAuthModule = () => SysAuthProviderModule(),
    console: ISysConsoleModule = SysConsoleProviderModule(),
  ) {
    this.auth = auth
    this.console = console
  }

  public static getInstance(): ISysGroupsModule {
    if (!SysGroupsV1.instance) {
      SysGroupsV1.instance = new SysGroupsV1()
    }
    return SysGroupsV1.instance
  }

  init(): boolean {
    return true
  }

  async loadGroups(): Promise<void> {
    const response = await new GetGroupsRoute().exec(null)
    if (this._isOk(response, 'groups')) {
      this._ownStore().groups = response.getData()
    }
  }

  getGroups(): IGroup[] {
    return this._ownStore().groups
  }

  async createGroup(title: string): Promise<IGroup | null> {
    const response = await new CreateGroupRoute().exec(new CreateGroupParams({ title }))
    if (!this._isOk(response, 'create group')) {
      return null
    }
    this._ownStore().groups = [...this.store.groups, response.getData()]

    return response.getData()
  }

  async renameGroup(groupUuid: string, title: string): Promise<boolean> {
    const response = await new RenameGroupRoute(groupUuid).exec(new RenameGroupParams({ uuid: groupUuid, title }))
    if (!this._isOk(response, 'rename group')) {
      return false
    }
    await this.loadGroups()

    return true
  }

  async removeGroup(groupUuid: string): Promise<boolean> {
    const response = await new RemoveGroupRoute(groupUuid).exec(null)
    if (!this._isOk(response, 'remove group')) {
      return false
    }
    this._forgetGroup(groupUuid)
    await this.loadGroups()

    return true
  }

  async loadMembers(groupUuid: string): Promise<void> {
    const response = await new GetMembersRoute(groupUuid).exec(null)
    if (this._isOk(response, 'members')) {
      this._ownStore().members = { ...this.store.members, [groupUuid]: response.getData() }
    }
  }

  getMembers(groupUuid: string): IMember[] {
    return this._ownStore().members[groupUuid] ?? []
  }

  async confirmMember(groupUuid: string, memberUuid: string): Promise<boolean> {
    const response = await new ConfirmMemberRoute(groupUuid, memberUuid).exec(null)
    if (!this._isOk(response, 'confirm member')) {
      return false
    }
    await this.loadMembers(groupUuid)

    return true
  }

  async removeMember(groupUuid: string, memberUuid: string): Promise<boolean> {
    const response = await new RemoveMemberRoute(groupUuid, memberUuid).exec(null)
    if (!this._isOk(response, 'remove member')) {
      return false
    }
    await this.loadMembers(groupUuid)

    return true
  }

  async leaveGroup(groupUuid: string): Promise<boolean> {
    const response = await new LeaveGroupRoute(groupUuid).exec(null)
    if (!this._isOk(response, 'leave group')) {
      return false
    }
    this._forgetGroup(groupUuid)
    await this.loadGroups()

    return true
  }

  async loadInvites(groupUuid: string): Promise<void> {
    const response = await new GetInvitesRoute(groupUuid).exec(null)
    if (this._isOk(response, 'invites')) {
      this._ownStore().invites = { ...this.store.invites, [groupUuid]: response.getData() }
    }
  }

  getInvites(groupUuid: string): IInvite[] {
    return this._ownStore().invites[groupUuid] ?? []
  }

  async createInvite(groupUuid: string, expiresAt: string | null): Promise<IInvite | null> {
    const response = await new CreateInviteRoute(groupUuid).exec(new CreateInviteParams({ uuid: groupUuid, expiresAt }))
    if (!this._isOk(response, 'create invite')) {
      return null
    }
    this._ownStore().invites = { ...this.store.invites, [groupUuid]: [...this.getInvites(groupUuid), response.getData()] }

    return response.getData()
  }

  async removeInvite(groupUuid: string, inviteUuid: string): Promise<boolean> {
    const response = await new RemoveInviteRoute(groupUuid, inviteUuid).exec(null)
    if (!this._isOk(response, 'remove invite')) {
      return false
    }
    await this.loadInvites(groupUuid)

    return true
  }

  async applyInvite(inviteUuid: string): Promise<IGroup | null> {
    const response = await new ApplyInviteRoute().exec(new ApplyInviteParams({ invite: inviteUuid }))
    if (!this._isOk(response, 'apply invite')) {
      return null
    }
    await this.loadGroups()

    return response.getData()
  }

  isMember(groupUuid: string): boolean {
    const role = this.getGroups().find((group) => group.uuid === groupUuid)?.role

    return role === EnumGroupRole.OWNER || role === EnumGroupRole.MEMBER
  }

  getViewPath(): string {
    return '/groups/v1/view/index.vue'
  }

  /**
   * Store отдаёт данные только той учётной записи, для которой они загружены: после сброса
   * сеанса или входа другой учётной записи прежние группы не видны.
   */
  _ownStore(): IStore {
    const userUuid = this.auth().getUserUuid()
    if (this.store.userUuid !== userUuid) {
      this.store.userUuid = userUuid
      this.store.groups = []
      this.store.members = {}
      this.store.invites = {}
    }

    return this.store
  }

  _forgetGroup(groupUuid: string): void {
    const store = this._ownStore()
    store.members = Object.fromEntries(Object.entries(store.members).filter(([uuid]) => uuid !== groupUuid))
    store.invites = Object.fromEntries(Object.entries(store.invites).filter(([uuid]) => uuid !== groupUuid))
  }

  _isOk<T>(response: ApiResponse<T>, action: string): boolean {
    if (response.isSuccess()) {
      return true
    }
    this.console.log(`Groups: ${action} failed: ${response.getError()}`, EnumConsoleType.ERROR)

    return false
  }
}

export default function (): ISysGroupsModule {
  return SysGroupsV1.getInstance()
}
